import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'professor' | 'student';
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  refreshAuth: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = useCallback(async (): Promise<User | null> => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return null;
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    try {
      const response = await api.get('/auth/me');
      if (response.data && response.data.user) {
        const userData = response.data.user;
        const currentUser = { id: userData._id, email: userData.email, name: userData.name, role: userData.role };
        setUser(currentUser);
        return currentUser;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUser(null);
      localStorage.removeItem('token');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Déconnexion réussie');
  }, []);

  const refreshAuth = useCallback(async (): Promise<User | null> => {
    try {
      const refreshResponse = await api.post('/auth/refresh');
      if (refreshResponse.data && refreshResponse.data.token) {
        localStorage.setItem('token', refreshResponse.data.token);
        api.defaults.headers.common['Authorization'] = `Bearer ${refreshResponse.data.token}`;
        return await fetchCurrentUser();
      } else {
        logout();
        return null;
      }
    } catch (refreshError) {
      console.error('Error refreshing token:', refreshError);
      logout();
      return null;
    }
  }, [fetchCurrentUser, logout]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [fetchCurrentUser]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
        const response = await api.post('/auth/login', { email, password });
        if (response.data && response.data.token) {
          const { token, user: userData } = response.data;
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify({ id: userData.id, email: userData.email, name: userData.name, role: userData.role }));
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          setUser({ id: userData.id, email: userData.email, name: userData.name, role: userData.role });
          toast.success('Connexion réussie !');
          return true;
        }
        return false;
    } catch (error: any) {
      // Diagnostic precis : on remonte la vraie raison de l'echec (401, reseau, serveur)
      const status = error?.response?.status;
      const apiMessage = error?.response?.data?.message;
      console.error('[Login] echec', status || error?.message || 'inconnu');
      if (status === 401 || status === 403) {
        toast.error(apiMessage || 'Identifiants invalides ou compte désactivé.');
      } else if (!error?.response) {
        toast.error('Serveur injoignable. Vérifie ta connexion puis réessaie.');
      } else {
        toast.error(apiMessage || `Erreur de connexion (code ${status}).`);
      }
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
};